import { PencilIcon } from "@heroicons/react/outline";
import { PlayIcon, TagIcon, TrashIcon } from "@heroicons/react/solid";
import { GitMerge, Hash } from "lucide-react";

import { useEvent } from "./changelog-item-context";

import { EntityIcon } from "@/components/ui/icons";
import { ActionType, EntityType } from "@/queries";

interface TimelineIconProps {
  actionType: ActionType;
}
const TimelineIcon = ({ actionType }: TimelineIconProps) => {
  const { approved, entity_type, approved_by, merged } = useEvent();

  const isDestructive = actionType.includes("DELETE") && !actionType.includes("DEPLOY");
  const isCreate = actionType.includes("CREATE") && !actionType.includes("VERSION");
  const isDeploy = actionType === ActionType.DEPLOY_LABEL;
  const isLabel = actionType.toLowerCase().includes("label") && !isDeploy;
  const isExecution = actionType.toLowerCase().includes("run");
  const isDataset = actionType.toLowerCase().includes("dataset");
  const isDirectCreate = actionType.toLowerCase() === "create";
  const isRename = actionType.toLowerCase() === "rename";
  const isMove = actionType.toLowerCase().includes("move");

  const isPending = !approved && !approved_by && !merged;
  const isDenied = approved_by && !approved;
  const isApproved = approved_by && approved;
  const isMerged = (merged && approved_by) || actionType.includes("DEPLOY");

  const getBorderColorClass = () => {
    switch (true) {
      case isPending:
        return "border-orange-500";
      case isDenied:
        return "border-gray-400";
      case isDestructive:
        return "border-red-500";
      case isCreate || isApproved || isMove:
        return "border-green-500";
      case isMerged:
        return "border-purple-500";
      default:
        return "border-gray-200";
    }
  };

  const getTextColorClass = () => {
    switch (true) {
      case isPending:
        return "text-orange-500";
      case isDenied:
        return "text-gray-400";
      case isDestructive:
        return "text-red-500";
      case isCreate || isApproved || isMove:
        return "text-green-500";
      case isMerged:
        return "text-purple-500";
      default:
        return "text-blue-500";
    }
  };

  const iconColor = getTextColorClass();
  const borderColor = getBorderColorClass();

  const renderIcon = () => {
    const iconClass = `h-4 w-4 ${iconColor}`;

    switch (true) {
      case isRename:
        return <PencilIcon className={iconClass} />;
      case isDeploy:
        return <GitMerge className={iconClass} />;
      case isDirectCreate:
        return <EntityIcon type={entity_type} />;
      case isDataset:
        return <EntityIcon type={EntityType.DATASET} />;
      case isExecution:
        return <PlayIcon className={iconClass} />;
      case isDestructive:
        return <TrashIcon className={iconClass} />;
      case isLabel:
        return <TagIcon className={iconClass} />;
      default:
        return <Hash className={iconClass} />;
    }
  };

  return (
    <div className="flex-shrink-0">
      <div
        className={`flex h-8 w-8 items-center justify-center rounded-full border-2 bg-white ${borderColor}`}
      >
        {renderIcon()}
      </div>
    </div>
  );
};

export default TimelineIcon;
